import { Star, MapPin, Box, Heart } from "lucide-react";
import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useToast } from "@/hooks/use-toast";
import { DeliveryToggle } from "@/components/DeliveryToggle";
import { useCart } from "@/contexts/CartContext";
import { AvailabilitySlots } from "./AvailabilitySlots";
import { AvailabilityDisplay } from "./AvailabilityDisplay";
import { OnDemandAvailability } from "./OnDemandAvailability";
import { formatDistance } from "@/utils/distance";
import { useIsMobile } from "@/hooks/use-mobile";
import { useAuth } from "@/contexts/AuthContext";

interface AvailabilitySlot {
  date: string;
  endDate?: string;
  slots: string[];
  recurring?: {
    days: string[];
    until: string;
  };
}

interface MealBasicInfoProps {
  name: string;
  description?: string;
  rating: number;
  reviews: number;
  distance: string;
  deliveryFee: number;
  mealId: string;
  price: number;
  availablePortions?: number;
  availabilitySlots?: AvailabilitySlot[];
  chef?: {
    id: string;
    first_name: string;
    last_name: string;
  };
  pickupInstructions?: string;
  deliveryAvailable?: boolean;
  preparationType?: 'scheduled' | 'on_demand';
  prepTimeHours?: number;
  cutoffTime?: string;
  pickupWindowStart?: string;
  pickupWindowEnd?: string;
  availableDays?: string[];
  maxDailyOrders?: number;
  portionSizeValue?: number;
  portionSizeUnit?: string;
  image?: string;
}

export const MealBasicInfo = ({
  name,
  description,
  rating,
  reviews,
  distance,
  deliveryFee,
  mealId,
  price,
  availablePortions,
  availabilitySlots = [],
  chef,
  pickupInstructions,
  deliveryAvailable = false,
  preparationType = 'scheduled',
  prepTimeHours,
  cutoffTime,
  pickupWindowStart,
  pickupWindowEnd,
  availableDays = [],
  maxDailyOrders,
  portionSizeValue,
  portionSizeUnit,
  image
}: MealBasicInfoProps) => {
  const [isFavorite, setIsFavorite] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [deliveryMethod, setDeliveryMethod] = useState(deliveryAvailable ? "delivery" : "pickup");
  const { toast } = useToast();
  const { addItem } = useCart();
  const isMobile = useIsMobile();
  const { user, setAuthModalOpen, setPendingCartItem } = useAuth();

  const isOnDemand = preparationType === 'on_demand';

  useEffect(() => {
    const checkIfFavorite = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { data } = await supabase
        .from('favorites')
        .select('id')
        .eq('user_id', user.id)
        .eq('meal_id', mealId)
        .maybeSingle();

      setIsFavorite(!!data);
    };

    checkIfFavorite();
  }, [mealId]);

  const toggleFavorite = async () => {
    try {
      setIsLoading(true);
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) {
        toast({
          title: "Error",
          description: "Please sign in to save favorites",
          variant: "destructive",
        });
        return;
      }

      if (isFavorite) {
        await supabase
          .from('favorites')
          .delete()
          .eq('user_id', user.id)
          .eq('meal_id', mealId);
      } else {
        await supabase
          .from('favorites')
          .insert({ user_id: user.id, meal_id: mealId });
      }

      setIsFavorite(!isFavorite);
      toast({
        title: isFavorite ? "Removed from favorites" : "Added to favorites",
        description: isFavorite ? "This meal has been removed from your favorites" : "This meal has been added to your favorites",
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to update favorites",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const transformAvailabilitySlots = (slots: AvailabilitySlot[]) => {
    return slots.map(slot => {
      const [startTime, endTime] = slot.slots[0].split(' - ');
      return {
        dateRange: {
          from: slot.date,
          to: slot.endDate || slot.date
        },
        startTime,
        endTime,
        availablePortions: availablePortions,
        ...(slot.recurring && {
          recurring: {
            days: slot.recurring.days,
            until: slot.recurring.until
          }
        })
      };
    });
  };

  const handleAddToCart = async () => {
    // Check if user is authenticated
    if (!user) {
      console.log("User not authenticated, showing auth modal");
      
      // Create a pending cart item with all the necessary information
      setPendingCartItem({
        id: mealId,
        name,
        price,
        image: image || '',
        chef_id: chef?.id || '',
        chef: `${chef?.first_name || ''} ${chef?.last_name || ''}`.trim() || 'Anonymous Chef',
        deliveryFee: deliveryMethod === "delivery" ? deliveryFee : 0,
        delivery_available: deliveryAvailable || false,
        availability_slots: transformAvailabilitySlots(availabilitySlots),
        pickupInstructions: deliveryMethod === "pickup" ? pickupInstructions : undefined,
        preparation_type: preparationType,
        prep_time_hours: prepTimeHours?.toString(),
        cutoff_time: cutoffTime,
        pickup_window_start: pickupWindowStart,
        pickup_window_end: pickupWindowEnd,
        available_days: availableDays,
        max_daily_orders: maxDailyOrders?.toString()
      });
      
      // Open the auth modal
      setAuthModalOpen(true);
      return;
    }

    const transformedSlots = transformAvailabilitySlots(availabilitySlots);
    
    let imageUrl = image || '';
    
    console.log('Adding to cart with image:', imageUrl);

    addItem({
      id: mealId,
      name,
      price,
      quantity: 1,
      image: imageUrl,
      chef_id: chef?.id || '',
      chef: `${chef?.first_name || ''} ${chef?.last_name || ''}`.trim() || 'Anonymous Chef',
      deliveryFee: deliveryMethod === "delivery" ? deliveryFee : 0,
      delivery_available: deliveryAvailable || false,
      availability_slots: transformedSlots,
      pickupInstructions: deliveryMethod === "pickup" ? pickupInstructions : undefined,
      preparation_type: preparationType,
      prep_time_hours: prepTimeHours?.toString(),
      cutoff_time: cutoffTime,
      pickup_window_start: pickupWindowStart,
      pickup_window_end: pickupWindowEnd,
      available_days: availableDays,
      max_daily_orders: maxDailyOrders?.toString()
    });

    toast({
      title: "Added to cart",
      description: `${name} has been added to your cart`,
    });
  };

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between">
        <div className="space-y-2">
          <h1 className="text-2xl font-bold">{name}</h1>
          <div className="flex items-center gap-1">
            <Star className="h-4 w-4 text-yellow-400 fill-current" />
            <span className="text-sm">{rating.toFixed(1)} ({reviews} reviews)</span>
          </div>
        </div>
        <Button
          variant="ghost"
          size="icon"
          onClick={toggleFavorite}
          disabled={isLoading}
          className={isFavorite ? "text-red-500" : "text-gray-500"}
        >
          <Heart className="w-6 h-6" fill={isFavorite ? "currentColor" : "none"} />
        </Button>
      </div>

      {description && (
        <p className="text-sm text-muted-foreground">{description}</p>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
        <div className="space-y-3">
          <div className="flex items-center gap-2">
            <Badge variant="secondary" className="text-sm px-3 py-0.5">
              ${price.toFixed(2)}
            </Badge>
            {deliveryAvailable && deliveryMethod === "delivery" && (
              <span className="text-sm text-muted-foreground">
                + ${deliveryFee.toFixed(2)} delivery
              </span>
            )}
          </div>

          {distance && (
            <div className="flex items-center gap-2 text-muted-foreground">
              <MapPin className="h-4 w-4" />
              <span>
                {typeof distance === 'string' && distance.includes(' mi') 
                  ? formatDistance(parseFloat(distance)) 
                  : distance}
              </span>
            </div>
          )}

          {availablePortions !== null && availablePortions > 0 && (
            <div className="flex items-center gap-2 text-muted-foreground">
              <Box className="h-4 w-4" />
              <span>{availablePortions} portions left</span>
            </div>
          )}
        </div>
      </div>

      <DeliveryToggle
        value={deliveryMethod}
        onValueChange={setDeliveryMethod}
        deliveryAvailable={deliveryAvailable}
      />

      {isOnDemand ? (
        cutoffTime && pickupWindowStart && pickupWindowEnd && (
          <div className="border rounded-lg p-3 bg-muted/50">
            <OnDemandAvailability
              config={{
                prep_time_hours: prepTimeHours || 0,
                cutoff_time: cutoffTime,
                pickup_window_start: pickupWindowStart,
                pickup_window_end: pickupWindowEnd,
                available_days: availableDays,
                max_daily_orders: maxDailyOrders
              }}
            />
          </div>
        )
      ) : (
        availabilitySlots && availabilitySlots.length > 0 && (
          <div className="border rounded-lg p-3 bg-muted/50">
            <AvailabilityDisplay slots={availabilitySlots} />
          </div>
        )
      )}

      <Button 
        size="lg" 
        className="w-full text-base py-6"
        onClick={handleAddToCart}
        disabled={availablePortions !== null && availablePortions === 0}
      >
        {availablePortions !== null && availablePortions === 0 ? "Sold Out" : "Add to Cart"}
      </Button>
    </div>
  );
};
